/**
 * Role Service
 * CRUD operations for role table
 */

import { getSupabaseClient } from '@/utils/supabase';
import { Role, PermissionObject } from '@/types/permission';

export interface CreateRoleData {
  role_name: string;
  permissions: PermissionObject[];
}

export interface UpdateRoleData {
  role_name?: string;
  permissions?: PermissionObject[];
}

/**
 * Get all roles
 */
export async function getAllRoles(): Promise<{
  success: boolean;
  data: Role[];
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('role')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching roles:', error);
      return {
        success: false,
        data: [],
        message: error.message,
      };
    }

    return {
      success: true,
      data: data as Role[],
    };
  } catch (error: any) {
    console.error('Fatal error fetching roles:', error);
    return {
      success: false,
      data: [],
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Get role by ID
 */
export async function getRoleById(roleId: string): Promise<{
  success: boolean;
  data: Role | null;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('role')
      .select('*')
      .eq('role_id', roleId)
      .single();

    if (error) {
      console.error('Error fetching role:', error);
      return {
        success: false,
        data: null,
        message: error.message,
      };
    }

    return {
      success: true,
      data: data as Role,
    };
  } catch (error: any) {
    console.error('Fatal error fetching role:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Create new role
 */
export async function createRole(roleData: CreateRoleData): Promise<{
  success: boolean;
  data: Role | null;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
    const newRole = {
      role_name: roleData.role_name,
      permissions: roleData.permissions,
      created_at: now,
      updated_at: now,
    };

    const { data, error } = await supabase
      .from('role')
      .insert(newRole)
      .select()
      .single();

    if (error) {
      console.error('Error creating role:', error);
      return {
        success: false,
        data: null,
        message: error.message,
      };
    }

    return {
      success: true,
      data: data as Role,
      message: 'Role created successfully',
    };
  } catch (error: any) {
    console.error('Fatal error creating role:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Update role
 */
export async function updateRole(
  roleId: string,
  updates: UpdateRoleData
): Promise<{
  success: boolean;
  data: Role | null;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const updateData = {
      ...updates,
      updated_at: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
    };

    const { data, error } = await supabase
      .from('role')
      .update(updateData)
      .eq('role_id', roleId)
      .select()
      .single();

    if (error) {
      console.error('Error updating role:', error);
      return {
        success: false,
        data: null,
        message: error.message,
      };
    }

    return {
      success: true,
      data: data as Role,
      message: 'Role updated successfully',
    };
  } catch (error: any) {
    console.error('Fatal error updating role:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Delete role
 */
export async function deleteRole(roleId: string): Promise<{
  success: boolean;
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    // First, check if any users have this role
    const { data: userRoles, error: checkError } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role_id', roleId);

    if (checkError) {
      console.error('Error checking user roles:', checkError);
      return {
        success: false,
        message: checkError.message,
      };
    }

    if (userRoles && userRoles.length > 0) {
      return {
        success: false,
        message: `Cannot delete role: ${userRoles.length} user(s) still have this role`,
      };
    }

    // Delete the role
    const { error } = await supabase
      .from('role')
      .delete()
      .eq('role_id', roleId);

    if (error) {
      console.error('Error deleting role:', error);
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: true,
      message: 'Role deleted successfully',
    };
  } catch (error: any) {
    console.error('Fatal error deleting role:', error);
    return {
      success: false,
      message: error.message || 'Unknown error',
    };
  }
}

/**
 * Get users by role ID
 */
export async function getUsersByRole(roleId: string): Promise<{
  success: boolean;
  data: any[];
  message?: string;
}> {
  try {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        user_id,
        users:user_id (
          user_id,
          first_name,
          last_name,
          avatar_url,
          stations,
          created_at
        )
      `)
      .eq('role_id', roleId);

    if (error) {
      console.error('Error fetching users by role:', error);
      return {
        success: false,
        data: [],
        message: error.message,
      };
    }

    return {
      success: true,
      data: data || [],
    };
  } catch (error: any) {
    console.error('Fatal error fetching users by role:', error);
    return {
      success: false,
      data: [],
      message: error.message || 'Unknown error',
    };
  }
}

export const roleService = {
  getAllRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  getUsersByRole,
};
